const initQuoteForm = () => {
    const form = document.querySelector("[data-quote-form]");

    if (!form) {
        return;
    }

    // ==========================================================
    // ELEMENTS
    // ==========================================================

    const steps = form.querySelectorAll("[data-quote-step]");
    const progressSteps = form.querySelectorAll("[data-quote-progress-step]");
    const progressBars = form.querySelectorAll("[data-quote-progress-bar]");
    const energyInputs = form.querySelectorAll("[data-quote-energy]");
    const solutionInputs = form.querySelectorAll("[data-quote-electricity-solution]");
    const sectorInputs = form.querySelectorAll("[data-quote-sector]");
    const genericChoices = form.querySelectorAll("[data-quote-generic-choice]");

    const electricityQuestion = form.querySelector('[data-quote-question="electricity-solution"]');
    const sectorQuestion = form.querySelector('[data-quote-question="sector"]');
    const siteQuestion = form.querySelector('[data-quote-question="site"]');
    const fuelNext = form.querySelector('[data-quote-question="fuel-next"]');

    const siteNumberInput = form.querySelector("[data-quote-site-number]");
    const siteNumberUnknown = form.querySelector("[data-quote-site-number-unknown]");
    const contractStart = form.querySelector("[data-quote-contract-start]");

    // ==========================================================
    // TOOLTIP STEP 1
    // ==========================================================

    const tooltipToggle = form.querySelector("[data-quote-tooltip-toggle]");
    const tooltip = form.querySelector("[data-quote-tooltip]");
    const tooltipContents = form.querySelectorAll("[data-quote-tooltip-energy]");

    // ==========================================================
    // STEP 3
    // ==========================================================

    const finalForm = form.querySelector('[data-quote-final="form"]');
    const fuelSuccess = form.querySelector('[data-quote-final="fuel-success"]');
    const energySuccess = form.querySelector('[data-quote-final="energy-success"]');

    const submitButton = form.querySelector("[data-quote-submit]");
    const submitMessage = form.querySelector("[data-quote-submit-message]");

    // ==========================================================
    // ETAT
    // ==========================================================

    let selectedEnergy = "";
    let selectedSolution = "";
    let isSubmitting = false;

    // ==========================================================
    // HELPERS
    // ==========================================================

    const showElement = (element) => {
        if (!element) {
            return;
        }

        element.classList.remove("hidden");

        requestAnimationFrame(() => {
            element.classList.add("is-visible");
        });
    };

    const hideElement = (element) => {
        if (!element) {
            return;
        }

        element.classList.remove("is-visible");
        element.classList.add("hidden");
    };

    const scrollToElement = (element) => {
        if (!element) {
            return;
        }

        window.setTimeout(() => {
            const isMobile = window.matchMedia("(max-width: 767px)").matches;
            const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            const rect = element.getBoundingClientRect();

            if (isMobile) {
                const offset = 20;
                const targetY = window.scrollY + rect.top - offset;

                window.scrollTo({
                    top: Math.max(0, targetY),
                    behavior: reducedMotion ? "instant" : "smooth",
                });

                return;
            }

            const viewportHeight = window.innerHeight;

            if (rect.top >= 0 && rect.top <= viewportHeight * 0.72) {
                return;
            }

            const scrollAmount = Math.min(Math.max(rect.top - viewportHeight * 0.62, 0), 300);

            if (scrollAmount <= 0) {
                return;
            }

            window.scrollBy({
                top: scrollAmount,
                behavior: reducedMotion ? "instant" : "smooth",
            });
        }, 150);
    };

    const resetQuestion = (question) => {
        if (!question) {
            return;
        }

        question.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
            input.checked = false;
        });

        question.querySelectorAll("[data-quote-choice]").forEach((choice) => {
            choice.classList.remove("is-active");
        });
    };

    const updateChoiceState = (input) => {
        if (!input) {
            return;
        }

        const container = input.closest("fieldset");

        if (!container) {
            return;
        }

        container.querySelectorAll("[data-quote-choice]").forEach((choice) => {
            choice.classList.remove("is-active");
        });

        const choice = input.closest("[data-quote-choice]");

        if (choice) {
            choice.classList.add("is-active");
        }
    };

    const getCheckedValue = (name) => {
        const input = form.querySelector(`input[name="${name}"]:checked`);
        return input?.value ?? "";
    };

    const getFieldValue = (name) => {
        const field = form.querySelector(`[name="${name}"]`);

        if (!field) {
            return "";
        }

        return String(field.value ?? "").trim();
    };

    const isChecked = (name) => {
        const field = form.querySelector(`[name="${name}"]`);
        return Boolean(field?.checked);
    };

    const setSummaryRow = (rowSelector, valueSelector, value) => {
        const row = form.querySelector(rowSelector);
        const valueElement = form.querySelector(valueSelector);

        if (!row || !valueElement) {
            return;
        }

        if (!value) {
            row.classList.add("hidden");
            valueElement.textContent = "";
            return;
        }

        valueElement.textContent = value;
        row.classList.remove("hidden");
    };

    // ==========================================================
    // PROGRESSION
    // ==========================================================

    const updateProgress = (currentStep, progress = null) => {
        if (progress === null) {
            const defaultProgress = {
                1: 0,
                2: 50,
                3: 100,
            };

            progress = defaultProgress[currentStep] ?? 0;
        }

        progress = Math.max(0, Math.min(100, progress));

        progressSteps.forEach((step) => {
            const number = Number(step.dataset.quoteProgressStep);
            const isComplete = number < currentStep;
            const isActive = number === currentStep;

            step.classList.toggle("is-complete", isComplete);
            step.classList.toggle("is-active", isActive);
        });

        progressBars.forEach((progressBar) => {
            const verticalTrack = progressBar.closest(".quote-form__progress-track");

            const horizontalTrack = progressBar.closest(".quote-form__progress-horizontal-track");

            if (verticalTrack) {
                progressBar.style.width = "100%";
                progressBar.style.height = `${progress}%`;
                return;
            }

            if (horizontalTrack) {
                progressBar.style.height = "100%";
                progressBar.style.width = `${progress}%`;
            }
        });
    };

    // ==========================================================
    // PROGRESSION INTERNE STEP 1
    // ==========================================================

    const updateStepOneProgress = () => {
        let progress = 0;

        if (selectedEnergy) {
            progress = 12;
        }

        if (selectedEnergy === "electricity") {
            if (selectedSolution) {
                progress = 24;
            }

            if (selectedSolution && getCheckedValue("sector")) {
                progress = 36;
            }
        }

        if (selectedEnergy === "gas" && getCheckedValue("sector")) {
            progress = 36;
        }

        if (selectedEnergy === "fuel") {
            progress = 36;
        }

        const siteNumber = String(siteNumberInput?.value ?? "").trim();

        if (selectedEnergy !== "fuel" && (siteNumber || siteNumberUnknown?.checked)) {
            progress = 46;
        }

        updateProgress(1, progress);
    };

    // ==========================================================
    // STEP 1 — TOOLTIP
    // ==========================================================

    const closeSiteTooltip = () => {
        if (tooltip) {
            tooltip.classList.add("hidden");
        }

        if (tooltipToggle) {
            tooltipToggle.setAttribute("aria-expanded", "false");
        }
    };

    const updateSiteTooltip = () => {
        if (!siteNumberInput) {
            return;
        }

        if (selectedEnergy === "gas") {
            siteNumberInput.placeholder = "Numéro de PCE";
        } else {
            siteNumberInput.placeholder = "Numéro de PDL / PRM";
        }

        tooltipContents.forEach((content) => {
            const energy = content.dataset.quoteTooltipEnergy;
            content.classList.toggle("hidden", energy !== selectedEnergy);
        });

        closeSiteTooltip();
    };

    // ==========================================================
    // STEP 2 — BRANCHE
    // ==========================================================

    const updateStepTwoBranch = () => {
        const branches = form.querySelectorAll("[data-quote-branch]");

        branches.forEach((branch) => {
            branch.classList.add("hidden");
        });

        hideElement(contractStart);

        if (selectedEnergy === "electricity") {
            const branch = form.querySelector(`[data-quote-branch="${selectedSolution}"]`);

            if (branch) {
                branch.classList.remove("hidden");
            }

            if (selectedSolution === "green_electricity") {
                showElement(contractStart);
            }

            return;
        }

        if (selectedEnergy === "gas") {
            const branch = form.querySelector('[data-quote-branch="gas"]');

            if (branch) {
                branch.classList.remove("hidden");
            }

            showElement(contractStart);
            return;
        }

        if (selectedEnergy === "fuel") {
            const branch = form.querySelector('[data-quote-branch="fuel"]');

            if (branch) {
                branch.classList.remove("hidden");
            }

            showElement(contractStart);
        }
    };

    // ==========================================================
    // STEP 3 — LIBELLES
    // ==========================================================

    const getSolutionLabel = () => {
        if (selectedEnergy === "gas") {
            return "Gaz";
        }

        if (selectedEnergy === "fuel") {
            const fuelSelect = form.querySelector('[name="fuel_type"]');

            if (fuelSelect && fuelSelect.selectedIndex > 0) {
                return fuelSelect.options[fuelSelect.selectedIndex].text;
            }

            return "Carburant";
        }

        const solutionLabels = {
            green_electricity: "Électricité verte",
            self_consumption: "Autoconsommation",
            electric_mobility: "Mobilité électrique",
        };

        return solutionLabels[selectedSolution] ?? "Électricité";
    };

    const getContractLabel = () => {
        const value = getCheckedValue("contract_start");

        const labels = {
            asap: "Le plus rapidement possible",
            three_months: "Dans les 3 prochains mois",
            other: "Autre",
        };

        return labels[value] ?? "";
    };

    // ==========================================================
    // STEP 3 — RECAPITULATIF
    // ==========================================================

    const updateSummary = () => {
        const solutionElement = form.querySelector("[data-quote-summary-solution]");

        if (solutionElement) {
            solutionElement.textContent = getSolutionLabel();
        }

        // PDL / PRM / PCE

        const siteNumberRow = form.querySelector("[data-quote-summary-site-number-row]");

        const siteNumberLabel = form.querySelector("[data-quote-summary-site-number-label]");

        const siteNumberValue = form.querySelector("[data-quote-summary-site-number]");

        const siteNumber = siteNumberInput && !siteNumberUnknown?.checked ? String(siteNumberInput.value ?? "").trim() : "";

        if (siteNumberRow && siteNumberLabel && siteNumberValue) {
            if (siteNumber && (selectedEnergy === "electricity" || selectedEnergy === "gas")) {
                siteNumberLabel.textContent = selectedEnergy === "gas" ? "PCE :" : "PDL / PRM :";

                siteNumberValue.textContent = siteNumber;
                siteNumberRow.classList.remove("hidden");
            } else {
                siteNumberLabel.textContent = "";
                siteNumberValue.textContent = "";
                siteNumberRow.classList.add("hidden");
            }
        }

        // CONSOMMATION

        let consumption = "";

        if (selectedEnergy === "electricity" && selectedSolution === "green_electricity") {
            const unknown = form.querySelector('[name="electricity_consumption_unknown"]');

            if (unknown?.checked) {
                consumption = "Non renseignée";
            } else {
                const value = getFieldValue("electricity_consumption");

                if (value) {
                    consumption = `${value} MWh/an`;
                }
            }
        }

        if (selectedEnergy === "gas") {
            const unknown = form.querySelector('[name="gas_consumption_unknown"]');

            if (unknown?.checked) {
                consumption = "Non renseignée";
            } else {
                const value = getFieldValue("gas_consumption");

                if (value) {
                    consumption = `${value} MWh/an`;
                }
            }
        }

        if (selectedEnergy === "fuel") {
            const unknown = form.querySelector('[name="fuel_consumption_unknown"]');

            if (unknown?.checked) {
                consumption = "Non renseignée";
            } else {
                const value = getFieldValue("fuel_consumption");

                if (value) {
                    consumption = `${value} L/an`;
                }
            }
        }

        setSummaryRow("[data-quote-summary-consumption-row]", "[data-quote-summary-consumption]", consumption);

        // AUTOCONSOMMATION

        let surface = "";

        if (selectedEnergy === "electricity" && selectedSolution === "self_consumption") {
            const value = getFieldValue("self_consumption_surface");

            if (value) {
                surface = `${value} m²`;
            }
        }

        setSummaryRow("[data-quote-summary-surface-row]", "[data-quote-summary-surface]", surface);

        // MOBILITE ELECTRIQUE

        let vehicles = "";

        if (selectedEnergy === "electricity" && selectedSolution === "electric_mobility") {
            const value = getFieldValue("electric_vehicle_count");

            if (value) {
                const amount = Number(value);
                vehicles = `${value} véhicule${amount > 1 ? "s" : ""}`;
            }
        }

        setSummaryRow("[data-quote-summary-vehicles-row]", "[data-quote-summary-vehicles]", vehicles);

        // DEMARRAGE CONTRAT

        setSummaryRow("[data-quote-summary-contract-row]", "[data-quote-summary-contract]", getContractLabel());
    };

    // ==========================================================
    // STEP 3 — FIN GLOBALE
    // ==========================================================

    const showGlobalFinal = () => {
        hideElement(fuelSuccess);
        hideElement(energySuccess);

        updateSummary();
        showElement(finalForm);
    };

    // ==========================================================
    // STEP 3 — CONFIRMATION FINALE
    // ==========================================================

    const showFinalConfirmation = () => {
        hideElement(finalForm);
        hideElement(fuelSuccess);
        hideElement(energySuccess);

        if (selectedEnergy === "fuel") {
            showElement(fuelSuccess);
            scrollToElement(fuelSuccess);
            return;
        }

        showElement(energySuccess);
        scrollToElement(energySuccess);
    };

    // ==========================================================
    // ETAPES
    // ==========================================================

    const showStep = (stepNumber) => {
        let activeStep = null;

        steps.forEach((step) => {
            const number = Number(step.dataset.quoteStep);
            const active = number === stepNumber;

            step.classList.toggle("hidden", !active);

            if (active) {
                activeStep = step;
            }
        });

        updateProgress(stepNumber);

        if (stepNumber === 2) {
            updateStepTwoBranch();
        }

        if (stepNumber === 3) {
            showGlobalFinal();
        }

        window.scrollTo({
            top: form.offsetTop,
            behavior: "smooth",
        });

        return activeStep;
    };

    // ==========================================================
    // STEP 1 — ENERGIE
    // ==========================================================

    energyInputs.forEach((input) => {
        input.addEventListener("change", () => {
            updateChoiceState(input);

            selectedEnergy = input.value;
            selectedSolution = "";

            resetQuestion(electricityQuestion);
            resetQuestion(sectorQuestion);
            resetQuestion(siteQuestion);

            hideElement(electricityQuestion);
            hideElement(sectorQuestion);
            hideElement(siteQuestion);
            hideElement(fuelNext);

            updateSiteTooltip();
            updateStepOneProgress();

            if (selectedEnergy === "electricity") {
                showElement(electricityQuestion);
                scrollToElement(electricityQuestion);
                return;
            }

            if (selectedEnergy === "gas") {
                showElement(sectorQuestion);
                scrollToElement(sectorQuestion);
                return;
            }

            if (selectedEnergy === "fuel") {
                showElement(fuelNext);
                scrollToElement(fuelNext);
            }
        });
    });

    // ==========================================================
    // STEP 1 — SOLUTION ELECTRIQUE
    // ==========================================================

    solutionInputs.forEach((input) => {
        input.addEventListener("change", () => {
            updateChoiceState(input);

            selectedSolution = input.value;

            resetQuestion(sectorQuestion);
            resetQuestion(siteQuestion);

            hideElement(sectorQuestion);
            hideElement(siteQuestion);

            updateStepOneProgress();

            showElement(sectorQuestion);
            scrollToElement(sectorQuestion);
        });
    });

    // ==========================================================
    // STEP 1 — SECTEUR
    // ==========================================================

    sectorInputs.forEach((input) => {
        input.addEventListener("change", () => {
            updateChoiceState(input);

            showElement(siteQuestion);
            updateSiteTooltip();
            updateStepOneProgress();

            scrollToElement(siteQuestion);
        });
    });

    // ==========================================================
    // CARTES GENERIQUES
    // ==========================================================

    genericChoices.forEach((input) => {
        input.addEventListener("change", () => {
            updateChoiceState(input);
        });
    });

    // ==========================================================
    // NUMERO SITE
    // ==========================================================

    if (siteNumberInput) {
        siteNumberInput.addEventListener("input", () => {
            updateStepOneProgress();
        });
    }

    // ==========================================================
    // NUMERO SITE INCONNU
    // ==========================================================

    if (siteNumberUnknown && siteNumberInput) {
        const updateSiteNumberState = () => {
            const isUnknown = siteNumberUnknown.checked;

            siteNumberInput.disabled = isUnknown;
            siteNumberInput.classList.toggle("is-disabled", isUnknown);

            if (isUnknown) {
                siteNumberInput.value = "";
            }

            updateStepOneProgress();
        };

        siteNumberUnknown.addEventListener("change", updateSiteNumberState);

        updateSiteNumberState();
    }

    // ==========================================================
    // STEP 2 — CHECKBOX "JE NE CONNAIS PAS"
    // ==========================================================

    const unknownFields = [
        {
            checkbox: 'input[name="electricity_consumption_unknown"]',
            input: 'input[name="electricity_consumption"]',
        },
        {
            checkbox: 'input[name="gas_consumption_unknown"]',
            input: 'input[name="gas_consumption"]',
        },
        {
            checkbox: 'input[name="fuel_consumption_unknown"]',
            input: 'input[name="fuel_consumption"]',
        },
    ];

    unknownFields.forEach(({ checkbox, input }) => {
        const checkboxElement = form.querySelector(checkbox);
        const inputElement = form.querySelector(input);

        if (!checkboxElement || !inputElement) {
            return;
        }

        const updateInputState = () => {
            const isUnknown = checkboxElement.checked;

            inputElement.disabled = isUnknown;
            inputElement.classList.toggle("is-disabled", isUnknown);

            if (isUnknown) {
                inputElement.value = "";
            }
        };

        checkboxElement.addEventListener("change", updateInputState);
        updateInputState();
    });

    // ==========================================================
    // TOOLTIP STEP 1
    // ==========================================================

    if (tooltipToggle && tooltip) {
        tooltipToggle.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            const opening = tooltip.classList.contains("hidden");

            tooltip.classList.toggle("hidden", !opening);
            tooltipToggle.setAttribute("aria-expanded", opening ? "true" : "false");
        });
    }

    // ==========================================================
    // TOOLTIP CONFIRMATION GAZ / ELECTRICITE
    // ==========================================================

    const finalTooltipToggle = form.querySelector("[data-quote-final-tooltip-toggle]");

    const finalTooltip = form.querySelector("[data-quote-final-tooltip]");

    const closeFinalTooltip = () => {
        if (!finalTooltip) {
            return;
        }

        finalTooltip.classList.add("hidden");

        if (finalTooltipToggle) {
            finalTooltipToggle.setAttribute("aria-expanded", "false");
        }
    };

    if (finalTooltipToggle && finalTooltip) {
        finalTooltipToggle.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            const opening = finalTooltip.classList.contains("hidden");

            finalTooltip.classList.toggle("hidden", !opening);

            finalTooltipToggle.setAttribute("aria-expanded", opening ? "true" : "false");
        });
    }

    // ==========================================================
    // FERMETURE DES TOOLTIPS
    // ==========================================================

    document.addEventListener("click", (event) => {
        if (tooltip && tooltipToggle && !tooltip.classList.contains("hidden") && !tooltip.contains(event.target) && !tooltipToggle.contains(event.target)) {
            closeSiteTooltip();
        }

        if (finalTooltip && finalTooltipToggle && !finalTooltip.classList.contains("hidden") && !finalTooltip.contains(event.target) && !finalTooltipToggle.contains(event.target)) {
            closeFinalTooltip();
        }
    });

    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") {
            return;
        }

        closeSiteTooltip();
        closeFinalTooltip();
    });

    // ==========================================================
    // NAVIGATION
    // ==========================================================

    form.querySelectorAll("[data-quote-next]").forEach((button) => {
        button.addEventListener("click", () => {
            const nextStep = Number(button.dataset.quoteNext);

            if (!nextStep) {
                return;
            }

            showStep(nextStep);
        });
    });

    // ==========================================================
    // VALIDATION STEP 3
    // ==========================================================

    const showSubmitError = (message) => {
        if (!submitMessage) {
            return;
        }

        submitMessage.textContent = message;
        submitMessage.classList.remove("hidden");
    };

    const clearSubmitError = () => {
        if (!submitMessage) {
            return;
        }

        submitMessage.textContent = "";
        submitMessage.classList.add("hidden");
    };

    const validateFinalForm = () => {
        const requiredFields = [
            {
                name: "last_name",
                label: "Nom",
            },
            {
                name: "first_name",
                label: "Prénom",
            },
            {
                name: "email",
                label: "Email professionnel",
            },
        ];

        let firstInvalid = null;

        form.querySelectorAll(".quote-form__input.is-error").forEach((field) => {
            field.classList.remove("is-error");
        });

        requiredFields.forEach(({ name }) => {
            const field = form.querySelector(`[name="${name}"]`);

            if (!field) {
                return;
            }

            const invalid = !String(field.value ?? "").trim();

            field.classList.toggle("is-error", invalid);

            if (invalid && !firstInvalid) {
                firstInvalid = field;
            }
        });

        const emailField = form.querySelector('[name="email"]');

        if (emailField && emailField.value.trim() && !emailField.validity.valid) {
            emailField.classList.add("is-error");

            if (!firstInvalid) {
                firstInvalid = emailField;
            }
        }

        if (firstInvalid) {
            firstInvalid.focus();

            showSubmitError(window.altensQuote?.messages?.requiredFields ?? "Merci de renseigner votre nom, votre prénom et une adresse email valide.");

            return false;
        }

        clearSubmitError();
        return true;
    };

    // ==========================================================
    // AJAX — CONSTRUCTION DES DONNEES
    // ==========================================================

    const buildQuoteFormData = () => {
        const data = new FormData();

        data.append("action", "altens_submit_quote");
        data.append("nonce", window.altensQuote?.nonce ?? "");

        // ------------------------------------------------------
        // STEP 1
        // ------------------------------------------------------

        data.append("energy", selectedEnergy);

        data.append("solution", selectedEnergy === "electricity" ? selectedSolution : "");

        data.append("sector", getCheckedValue("sector"));

        data.append("site_number_unknown", siteNumberUnknown?.checked ? "1" : "0");

        data.append("site_number", siteNumberUnknown?.checked ? "" : String(siteNumberInput?.value ?? "").trim());

        // ------------------------------------------------------
        // STEP 2 — CARBURANT
        // ------------------------------------------------------

        data.append("fuel_type", selectedEnergy === "fuel" ? getFieldValue("fuel_type") : "");

        // ------------------------------------------------------
        // STEP 2 — CONSOMMATION ELECTRICITE
        // ------------------------------------------------------

        const electricityUnknown = isChecked("electricity_consumption_unknown");

        data.append("electricity_consumption_unknown", electricityUnknown ? "1" : "0");

        data.append("electricity_consumption", electricityUnknown ? "" : getFieldValue("electricity_consumption"));

        // ------------------------------------------------------
        // STEP 2 — CONSOMMATION GAZ
        // ------------------------------------------------------

        const gasUnknown = isChecked("gas_consumption_unknown");

        data.append("gas_consumption_unknown", gasUnknown ? "1" : "0");

        data.append("gas_consumption", gasUnknown ? "" : getFieldValue("gas_consumption"));

        // ------------------------------------------------------
        // STEP 2 — CONSOMMATION CARBURANT
        // ------------------------------------------------------

        const fuelUnknown = isChecked("fuel_consumption_unknown");

        data.append("fuel_consumption_unknown", fuelUnknown ? "1" : "0");

        data.append("fuel_consumption", fuelUnknown ? "" : getFieldValue("fuel_consumption"));

        // ------------------------------------------------------
        // STEP 2 — AUTOCONSOMMATION
        // ------------------------------------------------------

        data.append("site_nature", selectedSolution === "self_consumption" ? getCheckedValue("site_nature") : "");

        data.append("self_consumption_surface", selectedSolution === "self_consumption" ? getFieldValue("self_consumption_surface") : "");

        // ------------------------------------------------------
        // STEP 2 — MOBILITE ELECTRIQUE
        // ------------------------------------------------------

        data.append("electric_vehicle_count", selectedSolution === "electric_mobility" ? getFieldValue("electric_vehicle_count") : "");

        // ------------------------------------------------------
        // STEP 2 — DEMARRAGE CONTRAT
        // ------------------------------------------------------

        data.append("contract_start", getCheckedValue("contract_start"));

        // ------------------------------------------------------
        // STEP 3 — COORDONNEES
        // ------------------------------------------------------

        data.append("siret", getFieldValue("siret"));
        data.append("last_name", getFieldValue("last_name"));
        data.append("first_name", getFieldValue("first_name"));
        data.append("phone", getFieldValue("phone"));
        data.append("email", getFieldValue("email"));

        return data;
    };

    const getRecaptchaToken = () => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha || !window.altensRecaptcha?.siteKey) {
                reject(new Error("Google reCAPTCHA est indisponible."));
                return;
            }

            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(window.altensRecaptcha.siteKey, {
                        action: "quote_form",
                    });

                    if (!token) {
                        throw new Error("Impossible de générer le jeton reCAPTCHA.");
                    }

                    resolve(token);
                } catch (error) {
                    reject(error);
                }
            });
        });
    };

    // ==========================================================
    // AJAX — TELECHARGEMENT DU PDF
    // ==========================================================

    const downloadPdf = (base64, filename) => {
        if (!base64) {
            return;
        }

        try {
            const binary = window.atob(base64);
            const bytes = new Uint8Array(binary.length);

            for (let index = 0; index < binary.length; index += 1) {
                bytes[index] = binary.charCodeAt(index);
            }

            const blob = new Blob([bytes], {
                type: "application/pdf",
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = filename || "demande-devis-altens.pdf";
            link.style.display = "none";

            document.body.appendChild(link);

            link.click();
            link.remove();

            window.setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        } catch (error) {
            console.error("Altens Quote PDF:", error);
        }
    };

    // ==========================================================
    // AJAX — ETAT DU BOUTON
    // ==========================================================

    const setSubmittingState = (submitting) => {
        isSubmitting = submitting;

        if (!submitButton) {
            return;
        }

        submitButton.disabled = submitting;
        submitButton.classList.toggle("is-loading", submitting);

        submitButton.setAttribute("aria-busy", submitting ? "true" : "false");
    };

    // ==========================================================
    // ENVOI FINAL
    // ==========================================================

    form.addEventListener("submit", async (event) => {
        event.preventDefault();

        if (isSubmitting) {
            return;
        }

        if (!validateFinalForm()) {
            return;
        }

        clearSubmitError();

        // ------------------------------------------------------
        // CONFIGURATION AJAX
        // ------------------------------------------------------

        if (!window.altensQuote || !window.altensQuote.ajaxUrl || !window.altensQuote.nonce) {
            showSubmitError("Le formulaire ne peut pas être envoyé pour le moment. Merci de recharger la page.");

            return;
        }

        setSubmittingState(true);

        try {
            const formData = buildQuoteFormData();

            const recaptchaToken = await getRecaptchaToken();

            formData.append("g-recaptcha-response", recaptchaToken);

            const response = await fetch(window.altensQuote.ajaxUrl, {
                method: "POST",
                body: formData,
                credentials: "same-origin",
            });

            let result = null;

            try {
                result = await response.json();
            } catch {
                throw new Error(window.altensQuote?.messages?.genericError ?? "Une erreur est survenue. Merci de réessayer.");
            }

            if (!response.ok || !result?.success) {
                throw new Error(result?.data?.message ?? window.altensQuote?.messages?.genericError ?? "Une erreur est survenue. Merci de réessayer.");
            }

            // --------------------------------------------------
            // TELECHARGEMENT DU PDF
            // --------------------------------------------------

            if (result.data?.pdf) {
                downloadPdf(result.data.pdf, result.data?.filename);
            }

            // --------------------------------------------------
            // CONFIRMATION
            // --------------------------------------------------

            showFinalConfirmation();
        } catch (error) {
            console.error("Altens Quote:", error);

            showSubmitError(error?.message || window.altensQuote?.messages?.genericError || "Une erreur est survenue. Merci de réessayer.");

            scrollToElement(submitMessage);
        } finally {
            setSubmittingState(false);
        }
    });

    // ==========================================================
    // INITIALISATION
    // ==========================================================

    updateProgress(1, 0);
};

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuoteForm);
} else {
    initQuoteForm();
}
